/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '@hooks';
import { JOIN_URL, FLOX_LOGO_BLUE_SRC } from '@constants';
import {
  getPasswordStrength,
  PasswordStrengthBar,
} from '@components/PasswordStrengthBar';
import {
  UserIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';
import { PhoneNumberInput } from '../../tailwindComponents/PhoneNumberInput';

const joinValidationSchema = Yup.object({
  firstName: Yup.string()
    .min(3, 'First name must have at least 3 characters.')
    .required('A first name is required.'),
  lastName: Yup.string()
    .min(3, 'Last name must have at least 3 characters.')
    .required('A last name is required.'),
  mobilePhoneNumber: Yup.string()
    .matches(/^[0-9\s]{7,15}$/, {
      message: 'Please enter a valid phone number.',
    })
    .required('A mobile phone number is required.'),
  countryCode: Yup.string().required(),
  password: Yup.string()
    .min(8, 'Password must have at least 8 characters.')
    .required('A password is required.')
    .test({
      name: 'password-strength-test',
      test: (value, ctx) => {
        if (!(getPasswordStrength(value as string) > 1)) {
          return ctx.createError({
            message: 'Password is not strong enough.',
          });
        }
        return true;
      },
    }),
});

type JoinFormValues = Yup.InferType<typeof joinValidationSchema>;

export function Join() {
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFocus, setPasswordFocus] = useState(false);

  useDocumentTitle('FLOX - Join');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<JoinFormValues>({
    resolver: yupResolver(joinValidationSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      mobilePhoneNumber: '',
      countryCode: 'GB',
      password: '',
    },
  });

  const onSubmit = async (data: JoinFormValues) => {
    setIsLoading(true);
    setSubmitError('');

    // Get phone code from country code
    const phoneCodes: Record<string, string> = {
      GB: '+44',
      US: '+1',
      IE: '+353',
      FR: '+33',
      DE: '+49',
    };
    const phoneCode = phoneCodes[data.countryCode] || '+44';
    const fullPhoneNumber = `${phoneCode}${data.mobilePhoneNumber.replace(
      /\s/g,
      ''
    )}`;

    try {
      const { status } = await authHttp.post(JOIN_URL, {
        token: searchParams.get('token'),
        firstName: data.firstName,
        lastName: data.lastName,
        mobilePhoneNumber: fullPhoneNumber,
        password: data.password,
      });

      if (status === 200) {
        setAccountCreated(true);
        setTimeout(() => navigate('/noticeboard'), 3000);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Join error:', error);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden md:block md:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-login.png)' }}
      />

      {/* Form Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12'>
        <div className='w-full max-w-md'>
          {/* Logo */}
          <div className='flex justify-center mb-8'>
            <img
              src={FLOX_LOGO_BLUE_SRC}
              alt='Flox logo'
              className='h-14 w-auto'
            />
          </div>

          {/* Title */}
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              {accountCreated ? 'Account Created!' : 'Join your team'}
            </h1>
            <p className='text-gray-500'>
              {accountCreated
                ? 'Redirecting you to Flox...'
                : 'Complete your profile to get started'}
            </p>
          </div>

          {/* Card */}
          <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
            {/* Success State */}
            {accountCreated ? (
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-green-100 p-3'>
                    <CheckCircleIcon className='h-8 w-8 text-green-600' />
                  </div>
                </div>
                <p className='text-gray-600 mb-6'>
                  Your account has been created successfully. You will be
                  redirected to Flox in a few seconds.
                </p>
                <Button
                  type='button'
                  intent='primary'
                  size='lg'
                  className='w-full'
                  label='Start using Flox'
                  onClick={() => navigate('/noticeboard')}
                />
              </div>
            ) : (
              /* Form State */
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Error Message */}
                {submitError && (
                  <div className='rounded-lg bg-yellow-50 p-4 mb-6'>
                    <div className='flex'>
                      <ExclamationTriangleIcon className='h-5 w-5 text-yellow-400 flex-shrink-0' />
                      <p className='ml-3 text-sm text-yellow-700'>
                        {submitError}
                      </p>
                    </div>
                  </div>
                )}

                {/* First Name Field */}
                <div className='mb-4'>
                  <label
                    htmlFor='firstName'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    First name
                  </label>
                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field }) => (
                      <div className='relative'>
                        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                          <UserIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          {...field}
                          type='text'
                          id='firstName'
                          placeholder='John'
                          autoComplete='given-name'
                          disabled={isLoading}
                          className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                            errors.firstName
                              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                          } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        />
                      </div>
                    )}
                  />
                  {errors.firstName && (
                    <p className='mt-1.5 text-sm text-red-600'>
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                {/* Last Name Field */}
                <div className='mb-4'>
                  <label
                    htmlFor='lastName'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Last name
                  </label>
                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field }) => (
                      <div className='relative'>
                        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                          <UserIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          {...field}
                          type='text'
                          id='lastName'
                          placeholder='Doe'
                          autoComplete='family-name'
                          disabled={isLoading}
                          className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                            errors.lastName
                              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                          } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        />
                      </div>
                    )}
                  />
                  {errors.lastName && (
                    <p className='mt-1.5 text-sm text-red-600'>
                      {errors.lastName.message}
                    </p>
                  )}
                </div>

                {/* Mobile Phone Field */}
                <div className='mb-4'>
                  <Controller
                    name='mobilePhoneNumber'
                    control={control}
                    render={({ field }) => (
                      <Controller
                        name='countryCode'
                        control={control}
                        render={({ field: countryField }) => (
                          <PhoneNumberInput
                            label='Mobile phone number'
                            name='mobilePhoneNumber'
                            value={field.value}
                            onChange={field.onChange}
                            countryCode={countryField.value}
                            onCountryCodeChange={countryField.onChange}
                            placeholder='7700 900000'
                            disabled={isLoading}
                            isInvalid={!!errors.mobilePhoneNumber}
                            validationMessage={
                              errors.mobilePhoneNumber?.message
                            }
                          />
                        )}
                      />
                    )}
                  />
                </div>

                {/* Password Field */}
                <div className='mb-6'>
                  <label
                    htmlFor='password'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Password
                  </label>
                  <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                      <div className='relative'>
                        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                          <LockClosedIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          {...field}
                          type='password'
                          id='password'
                          placeholder='••••••••••••'
                          autoComplete='new-password'
                          disabled={isLoading}
                          className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                            errors.password
                              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                          } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                          onFocus={() => setPasswordFocus(true)}
                          onBlur={() => setPasswordFocus(false)}
                          onChange={(e) => {
                            field.onChange(e);
                            setPasswordStrength(
                              getPasswordStrength(e.target.value)
                            );
                          }}
                        />
                      </div>
                    )}
                  />
                  <p className='mt-1.5 text-xs text-gray-500'>
                    8 characters minimum, including a number.
                  </p>
                  {passwordFocus && (
                    <div className='mt-2'>
                      <PasswordStrengthBar score={passwordStrength} />
                    </div>
                  )}
                  {errors.password && (
                    <p className='mt-1.5 text-sm text-red-600'>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type='submit'
                  intent='primary'
                  size='lg'
                  disabled={!isValid || isLoading}
                  className='w-full'
                  label={isLoading ? 'Creating account...' : 'Create account'}
                />
              </form>
            )}
          </div>

          {/* Footer */}
          <div className='mt-8 text-center'>
            <p className='text-sm text-gray-400'>
              Need help? Contact{' '}
              <a
                href='mailto:support@flox.is'
                className='text-blue-600 hover:underline'
              >
                support@flox.is
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
